import store from '@/store';
import availablePaymentEngines from '../constants/PaymentEngine';

/**
 * Types of cybersource error codes
 * this should be passed in the setError action to show an error message
 * */
const cybersourceErrorCodes = {
  ach_verification_failed: 'cybersource.ach_verification_failed',
  address_country_watchlist_match: 'cybersource.address_country_watchlist_match',
  allowable_pin_retries_exceeded: 'cybersource.allowable_pin_retries_exceeded',
  avs_failed: 'cybersource.avs_failed',
  blacklisted_customer: 'cybersource.blacklisted_customer',
  blocked_by_cardholder: 'cybersource.blocked_by_cardholder',
  consumer_authentication_failed: 'cybersource.consumer_authentication_failed',
  consumer_authentication_required: 'cybersource.consumer_authentication_required',
  contact_processor: 'cybersource.contact_processor',
  customer_watchlist_match: 'cybersource.customer_watchlist_match',
  cv_failed: 'cybersource.cv_failed',
  cvn_not_match: 'cybersource.cvn_not_match',
  daggdenied: 'cybersource.daggdenied',
  daggrejected: 'cybersource.daggrejected',
  decision_profile_reject: 'cybersource.decision_profile_reject',
  decision_profile_review: 'cybersource.decision_profile_review',
  declined_check: 'cybersource.declined_check',
  dsysrejected: 'cybersource.dsysrejected',
  email_country_watchlist_match: 'cybersource.email_country_watchlist_match',
  exceeds_credit_limit: 'cybersource.exceeds_credit_limit',
  expired_card: 'cybersource.expired_card',
  fraud_rejected: 'cybersource.fraud_rejected',
  general_decline: 'cybersource.general_decline',
  insufficient_fund: 'cybersource.insufficient_fund',
  invalid_account: 'cybersource.invalid_account',
  invalid_cvn: 'cybersource.invalid_cvn',
  invalid_merchant_configuration: 'cybersource.invalid_merchant_configuration',
  ip_country_watchlist_match: 'cybersource.ip_country_watchlist_match',
  issuer_unavailable: 'cybersource.issuer_unavailable',
  payment_refused: 'cybersource.payment_refused',
  pending_authentication: 'cybersource.pending_authentication',
  processor_declined: 'cybersource.processor_declined',
  processor_error: 'cybersource.processor_error',
  score_exceeds_threshold: 'cybersource.score_exceeds_threshold',
  server_timeout: 'cybersource.server_timeout',
  service_timeout: 'cybersource.service_timeout',
  stolen_lost_card: 'cybersource.stolen_lost_card',
  suspended_account: 'cybersource.suspended_account',
  system_error: 'cybersource.system_error',
  unauthorized_card: 'cybersource.unauthorized_card',
  default: 'cybersource.default_error',
};

/**
 * Resolve the notification key for a Cybersource decline reason.
 * Own-property check so inherited keys (e.g. "toString", "constructor",
 * "__proto__") fall back to the default error instead of resolving
 * Object.prototype members.
 * @param {String} reason
 * @returns {String} notification key (e.g. 'cybersource.expired_card')
 */
export function getCybersourceErrorKey(reason) {
  return Object.prototype.hasOwnProperty.call(cybersourceErrorCodes, reason)
    ? cybersourceErrorCodes[reason]
    : cybersourceErrorCodes.default;
}

/**
 * Visible codes IAMSA approved for each family of Cybersource decline (MSI-1509). The code is part
 * of the message the passenger reads, so a CS agent can trace a report back to a family from the
 * screenshot alone. The comment on each entry is the Cybersource reason code it was mapped from.
 *
 * The translation catalog stays keyed by reason: every funnel locale must carry every reason, and
 * only es-MX and en-US ship the approved wording. So this map is the only place the five families
 * are stated, and a test asserts the two agree — each es-MX message starts with the code below.
 * */
const cybersourceDisplayCodes = {
  // 001 - the card issuer refused the charge
  contact_processor: '001', // 201 bank inquiries / card blocked
  expired_card: '001', // 202
  insufficient_fund: '001', // 204
  exceeds_credit_limit: '001', // 210

  // 002 - the acquirer (Santander) rejected the card data
  cv_failed: '002', // 200
  cvn_not_match: '002', // 209
  invalid_cvn: '002', // 211
  invalid_account: '002', // 231

  // 003 - nobody answered in time
  server_timeout: '003', // 151
  service_timeout: '003', // 152

  // 004 - the gateway itself failed
  processor_error: '004', // 150
  system_error: '004', // 150 general system failure
  processor_declined: '004', // 233

  // 005 - antifraud rejected the transaction. `fraud_rejected` is not a Cybersource reason: it is
  // the marker platform-purchase writes when Decision Manager hard-rejects without one of its own.
  fraud_rejected: '005',
  decision_profile_reject: '005',
  decision_profile_review: '005',
  score_exceeds_threshold: '005',
  blacklisted_customer: '005',
  customer_watchlist_match: '005',
  address_country_watchlist_match: '005',
  email_country_watchlist_match: '005',
  ip_country_watchlist_match: '005',
  daggrejected: '005',
  daggdenied: '005',
  dsysrejected: '005',
};

/**
 * Family code for reasons the approved catalog does not name. 004 is the only family that asserts
 * nothing about the card, the issuer or fraud screening, so it is also the default message.
 * */
const DEFAULT_DISPLAY_CODE = '004';

/**
 * Reduce a Cybersource decline reason to the token that classifies it, mirroring
 * `Cybersource::UserMessage.family_of` in platform-purchase. The API returns the reason exactly as
 * Cybersource wrote it: upper case, and sometimes carrying a sub-reason or the provider's own prose
 * after a colon ("INVALID_CVN", "CV_FAILED: Soft Decline - did not pass the CVN check."). Only the
 * leading token names the family. The leading colon goes first, so a reason that arrived as a
 * stringified symbol (":cv_failed") does not split into an empty token.
 * @param {String} reason
 * @returns {String} the classifying token, lower case
 */
function classifyingToken(reason) {
  return String(reason).replace(/^:/, '').split(':')[0].trim().toLowerCase();
}

/**
 * Resolve the IAMSA family code (001-005) for a Cybersource decline reason, for analytics: the raw
 * reason answers "which reason", this answers "which of the five messages the passenger saw". So it
 * has to classify the reason the same way the message did, whichever shape it arrived in — a
 * mismatch here reports 004 for a passenger who read 002.
 * Own-property check so inherited keys ("toString", "__proto__") do not resolve a family.
 * @param {String} reason
 * @returns {String|undefined} family code, or undefined when there is no reason to classify
 */
export function getCybersourceDisplayCode(reason) {
  if (!reason) return undefined;

  const token = classifyingToken(reason);

  return Object.prototype.hasOwnProperty.call(cybersourceDisplayCodes, token)
    ? cybersourceDisplayCodes[token]
    : DEFAULT_DISPLAY_CODE;
}

/**
 * Flag to indicate if it should use cybersource or not
 * @param {String} paymentEngine
 * @returns Boolean
 */
export function shouldUseCybersource(paymentEngine) {
  const cybersourceIsEnabled = store.getState().whitelabelConfig?.env?.cybersource?.enabled;
  return cybersourceIsEnabled && paymentEngine === availablePaymentEngines.cybersource;
}

/**
 * Decode the base64url-encoded payload segment of a JWT without verifying its signature.
 * @param {String} jwt - The JWT string (header.payload.signature)
 * @returns {Object|null} The decoded payload object, or null if it cannot be decoded
 */
function decodeJwtPayload(jwt) {
  try {
    const payloadSegment = jwt?.split('.')?.[1];
    if (!payloadSegment) return null;

    const base64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    return JSON.parse(atob(padded));
  } catch (error) {
    console.error('Failed to decode Cybersource transient token:', error);
    return null;
  }
}

/**
 * Extract the card BIN from a Cybersource transient token (microform JWT).
 * The microform hosted fields never expose raw digits in field events; the BIN
 * is only available in the transient token payload at
 * content.paymentInformation.card.number.bin.
 * @param {String} transientToken - The transient token JWT returned by microform.createToken
 * @returns {String|null} The BIN (typically 6 digits), or null if unavailable
 */
export function getBinFromCybersourceToken(transientToken) {
  const payload = decodeJwtPayload(transientToken);
  return payload?.content?.paymentInformation?.card?.number?.bin || null;
}

export default cybersourceErrorCodes;
